import cacheSmartTimeExtendedPropertyAction from '../actions/cacheSmartTimeExtendedPropertyAction';
import SmartTimeResponse from '../services/SmartTimeResponse';
import { ClientItem, SmartTimeExtendedProperty, SmartTimeTrigger } from 'owa-mail-store';
import ExtendedPropertyType from 'owa-service/lib/contract/ExtendedPropertyType';
import { trace } from 'owa-trace';
import { isFeatureEnabled } from 'owa-feature-flags';

const ENTITYID_KEY = '@EntityId';
const CONFIDENCE_KEY: string = 'confidence';
const SENDER_SCHEDULER_CONFIDENCE_KEY: string = 'SenderSchedulerConfidence';
const RECIPIENT_SCHEDULER_CONFIDENCE_KEY: string = 'RecipientSchedulerConfidence';
const UNCLEAR_SCHEDULER_CONFIDENCE_KEY: string = 'UnclearSchedulerConfidence';
const AVAILABILITY_CONFIDENCE_KEY: string = 'AvailabilityConfidence';
const TIMES_KEY = 'times';

export default function parseSmartTimeProperty(
    item: ClientItem,
    extendedProperty: ExtendedPropertyType
): void {
    if (!extendedProperty) {
        trace.info(
            '[getSmartTimeForItem_parseSmartTimeProperty] No smart time extended property found.'
        );
        return;
    }

    let smartTimeSuggestedProperty: SmartTimeExtendedProperty = null;
    let validProperty: boolean = false;

    try {
        const parsedProperty = JSON.parse(extendedProperty.Value) as SmartTimeResponse[];

        // Searching the parsedProperty for the item with the highest confidence score and consume its information.
        // An empty, null, or undefined confidence score will be considered as a 0 in calculation.
        let smartTimeConfidenceScore: number = -Infinity;
        let smartTimeResponse: SmartTimeResponse = null;
        parsedProperty.forEach(item => {
            const itemConfidenceScore: number = getConfidenceScore(item);
            if (itemConfidenceScore > smartTimeConfidenceScore) {
                smartTimeConfidenceScore = itemConfidenceScore;
                smartTimeResponse = item;
            }
        });
        const smartTimeEntityId: string = smartTimeResponse[ENTITYID_KEY];
        const smartTimeSuggestion = smartTimeResponse[TIMES_KEY][0];
        const smartTimeRawConfidenceScores = {
            confidence: smartTimeResponse[CONFIDENCE_KEY],
            AvailabilityConfidence: smartTimeResponse[AVAILABILITY_CONFIDENCE_KEY],
            RecipientSchedulerConfidence: smartTimeResponse[RECIPIENT_SCHEDULER_CONFIDENCE_KEY],
            SenderSchedulerConfidence: smartTimeResponse[SENDER_SCHEDULER_CONFIDENCE_KEY],
            UnclearSchedulerConfidence: smartTimeResponse[UNCLEAR_SCHEDULER_CONFIDENCE_KEY],
        };

        const smartTimeTrigger: SmartTimeTrigger = getSmartTimePillTrigger(
            smartTimeResponse,
            smartTimeConfidenceScore
        );
        smartTimeSuggestedProperty = {
            beginingOfTimeRange: smartTimeSuggestion.beginingOfTimeRange,
            endOfTimeRange: smartTimeSuggestion.endOfTimeRange,
            duration: smartTimeSuggestion.duration,
            entityId: smartTimeEntityId,
            hasIntent: true,
            confidenceScore: smartTimeConfidenceScore,
            scheduler: smartTimeTrigger,
            rawConfidenceScores: smartTimeRawConfidenceScores,
        };
        validProperty = true;
    } catch (e) {
        trace.warn(
            '[getSmartTimeForItem_parseSmartTimeProperty] Malformed property, cannot parse. Error: ' +
                e.message
        );
        (e as { networkError?: boolean }).networkError = true;
        throw e;
    } finally {
        cacheSmartTimeExtendedPropertyAction(item, validProperty, smartTimeSuggestedProperty);
    }
}

function getConfidenceScore(response: SmartTimeResponse): number {
    if (isFeatureEnabled('mc-smartReplyWithMeeting-MeetingScheduler')) {
        // Extract the highest confidence score in RecipientSchedulerConfidence, UnclearSchedulerConfidence,
        // and AvailabilityConfidence. SenderConfidence is not included.
        return Math.max(
            response[RECIPIENT_SCHEDULER_CONFIDENCE_KEY]
                ? response[RECIPIENT_SCHEDULER_CONFIDENCE_KEY]
                : 0,
            response[UNCLEAR_SCHEDULER_CONFIDENCE_KEY]
                ? response[UNCLEAR_SCHEDULER_CONFIDENCE_KEY]
                : 0,
            response[AVAILABILITY_CONFIDENCE_KEY] ? response[AVAILABILITY_CONFIDENCE_KEY] : 0
        );
    } else {
        // Extract the only confidence score
        return response[CONFIDENCE_KEY] ? +response[CONFIDENCE_KEY] : 0;
    }
}

function getSmartTimePillTrigger(
    response: SmartTimeResponse,
    smartTimeConfidenceScore: number
): SmartTimeTrigger {
    let smartTimeTrigger: SmartTimeTrigger = '';
    if (
        isFeatureEnabled('mc-smartReplyWithMeeting-MeetingScheduler') &&
        response &&
        smartTimeConfidenceScore > 0
    ) {
        let confidenceScore = 0;
        if (response[RECIPIENT_SCHEDULER_CONFIDENCE_KEY] > confidenceScore) {
            confidenceScore = response[RECIPIENT_SCHEDULER_CONFIDENCE_KEY];
            smartTimeTrigger = 'Recipient';
        }
        if (response[UNCLEAR_SCHEDULER_CONFIDENCE_KEY] > confidenceScore) {
            confidenceScore = response[UNCLEAR_SCHEDULER_CONFIDENCE_KEY];
            smartTimeTrigger = 'Unclear';
        }
        if (response[AVAILABILITY_CONFIDENCE_KEY] > confidenceScore) {
            confidenceScore = response[AVAILABILITY_CONFIDENCE_KEY];
            smartTimeTrigger = 'Availability';
        }
    }

    return smartTimeTrigger;
}
